<template>
  <el-dialog v-if="visible" :title="title" :visible.sync="visible" :before-close="handleClose">
    <div v-if="is_first_display">
      <CampaignCreateIntro @close="closeCampaignCreateIntro" />
    </div>

    <div v-else v-loading="is_loading">
      <div v-if="is_create && !is_campaign_created" class="dialog-campaign-step">
        <Steps :active="step" :options="step_options" />
      </div>

      <div v-if="isStep(0)">
        <div class="campaign-create-item">
          <CampaignCreateAccount />
        </div>
      </div>

      <div v-if="isStep(1)">
        <Message type="info">キャンペーンのタイトルを設定してください。</Message>
        <div class="campaign-create-item">
          <CampaignCreateTitle />
        </div>
      </div>

      <div v-if="isStep(2)">
        <Message type="info">
          キャンペーンの期間を設定してください。期間外の応募は収集されません。
          <br />
          <div v-if="is_twitter">キャンペーンポストは、必ず期間内に公開するようにしてください。</div>
          <div v-if="is_instagram || is_tiktok">キャンペーン投稿は、必ず期間内に公開するようにしてください。</div>
          <div class="note-message">
            ※実施済みキャンペーンの応募収集については
            <a href="https://help-atelu.comnico.jp/recollection-applicants" target="_blank">こちら</a>
            をご確認ください。
          </div>
        </Message>
        <div class="campaign-create-item">
          <CampaignCreatePeriod />
        </div>
      </div>

      <div v-if="isStep(3)">
        <Message type="info">応募条件を設定してください。</Message>
        <div class="campaign-create-item">
          <CampaignCreateTwitterCondition v-if="is_twitter" />
          <CampaignCreateInstagramCondition v-if="is_instagram" />
          <CampaignCreateTikTokCondition v-if="is_tiktok" />
        </div>
      </div>

      <div v-if="isStep(4) && !is_campaign_created">
        <CampaignCreateTwitterConfirm v-if="is_twitter" @cancel="handleClose" @complete="completedCreateCampaign" />
        <CampaignCreateInstagramConfirm v-if="is_instagram" @cancel="handleClose" @complete="completedCreateCampaign" />
        <CampaignCreateTikTokConfirm v-if="is_tiktok" @cancel="handleClose" @complete="completedCreateCampaign" />
      </div>

      <div v-if="is_campaign_created">
        <CampaignCreateCompleted @close="handleClose" />
      </div>

      <div v-if="is_show_footer" class="dialog-footer">
        <div>
          <el-button v-if="!isStep(0)" @click="prevStep">
            <Icon name="arrow-left" space="right" @click="prevStep" />
            戻る
          </el-button>
        </div>
        <div class="footer-right">
          <el-button @click="handleClose('cancel')">キャンセル</el-button>
          <el-button :disabled="is_next_disabled" type="primary" @click="nextStep">
            次へ
            <Icon name="arrow-right" space="left" />
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, ref } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import Message from '@/client/components/_atoms/Message.vue'
import Steps from '@/client/components/_atoms/Steps.vue'
import CampaignCreateTikTokCondition from '@/client/components/_organisms/campaign_create_tiktok_condition/Component.vue'
import CampaignCreateTikTokConfirm from '@/client/components/_organisms/campaign_create_tiktok_confirm/Component.vue'
import CampaignCreateAccount from '@/client/components/_organisms/CampaignCreateAccount.vue'
import CampaignCreateCompleted from '@/client/components/_organisms/CampaignCreateCompleted.vue'
import CampaignCreateInstagramCondition from '@/client/components/_organisms/CampaignCreateInstagramCondition.vue'
import CampaignCreateInstagramConfirm from '@/client/components/_organisms/CampaignCreateInstagramConfirm.vue'
import CampaignCreateIntro from '@/client/components/_organisms/CampaignCreateIntro.vue'
import CampaignCreatePeriod from '@/client/components/_organisms/CampaignCreatePeriod.vue'
import CampaignCreateTitle from '@/client/components/_organisms/CampaignCreateTitle.vue'
import CampaignCreateTwitterCondition from '@/client/components/_organisms/CampaignCreateTwitterCondition.vue'
import CampaignCreateTwitterConfirm from '@/client/components/_organisms/CampaignCreateTwitterConfirm.vue'
import { TrackingService } from '@/client/services'
import { useCampaignDialogStore } from '@/client/store/useCampaignDialogStore'
import { useSystemStore } from '@/client/store/useSystemStore'

const visible = ref(false)
const is_campaign_created = ref(false)
const is_first_display_closed = ref(false)
const campaignDialogStore = useCampaignDialogStore()
const { form, mode, step, is_loading, is_next_disabled, is_form_change } = storeToRefs(campaignDialogStore)

const systemStore = useSystemStore()
const user = computed(() => systemStore.user)

const setCampaignId = (id: number) => campaignDialogStore.setCampaignId(id)
const setMode = (m: 'create' | 'edit' | 'copy') => campaignDialogStore.setMode(m)
const fetchCampaign = () => campaignDialogStore.fetchCampaign()
const fetchCampaignAccounts = () => campaignDialogStore.fetchCampaignAccounts()
const reset = () => campaignDialogStore.reset()
const getDefaultKeywords = () => campaignDialogStore.getDefaultKeywords()
const nextStep = () => campaignDialogStore.nextStep()
const prevStep = () => campaignDialogStore.prevStep()

// 親へイベント通知
const emit = defineEmits<{ (e: 'campaignUpdated'): void }>()

// vue2 互換の $notify 利用（any）
const { proxy } = getCurrentInstance() as any

// 表示タイトル
const title = computed(() => (is_first_display.value ? 'はじめに' : 'キャンペーンの新規作成'))

// ステップラベル
const step_options = ['アカウント', 'タイトル', '期間', '応募条件', '確認']

// 各種判定
const is_create = computed(() => mode.value === 'create')
const is_first_display = computed(() => user.value && user.value.isFirstDisplay && !is_first_display_closed.value)
const is_twitter = computed(() => form.value.target_sns === 'twitter')
const is_instagram = computed(() => form.value.target_sns === 'instagram')
const is_tiktok = computed(() => form.value.target_sns === 'tiktok')
const is_show_footer = computed(() => step.value < 4)

// ステップ比較
function isStep(s: number) {
  return step.value === s
}

/**
 * ダイアログキャンペーンを開く
 */
async function open(campaignId?: number) {
  visible.value = true
  is_loading.value = true

  await fetchCampaignAccounts()

  if (!campaignId) {
    await setMode('create')
    await getDefaultKeywords()
  }

  if (campaignId) {
    await setCampaignId(campaignId)
    await setMode('copy')
    const response = await fetchCampaign()

    if (!response.data) {
      proxy.$notify({
        title: 'キャンペーンの取得に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger'
      })

      visible.value = false
      is_campaign_created.value = false
      is_first_display_closed.value = false
      reset()
      return
    }
  }

  is_loading.value = false
}

/**
 * ダイアログキャンペーンを閉じる (Element UI before-close 対応)
 * value は cancel 文字列、もしくは done コールバック関数などが渡る想定
 */
function handleClose(value?: any) {
  // トラッキングイベント送信
  switch (step.value) {
    case 0:
      TrackingService.sendEvent(
        `click:キャンペーンの新規作成|アカウント|${value === 'cancel' ? 'キャンセル' : '閉じる'}`
      )
      break
    case 1:
      TrackingService.sendEvent(`click:キャンペーンの新規作成|タイトル|${value === 'cancel' ? 'キャンセル' : '閉じる'}`)
      break
    case 2:
      TrackingService.sendEvent(`click:キャンペーンの新規作成|期間|${value === 'cancel' ? 'キャンセル' : '閉じる'}`)
      break
    case 3:
      TrackingService.sendEvent(
        `click:キャンペーンの新規作成|応募条件(${form.value.target_sns === 'twitter' ? 'X' : 'IG'})|${
          value === 'cancel' ? 'キャンセル' : '閉じる'
        }`
      )
      break
    case 4:
      if (typeof value === 'function') {
        TrackingService.sendEvent('click:キャンペーンの新規作成|確認|閉じる')
      }
      break
    default:
      break
  }

  if (!is_form_change.value || is_campaign_created.value) {
    visible.value = false
    is_campaign_created.value = false
    is_first_display_closed.value = false
    reset()
    return
  }

  const confirmClose = window.confirm('編集内容が保存されていません。ダイアログを閉じますか？')
  if (!confirmClose) return

  visible.value = false
  is_campaign_created.value = false
  is_first_display_closed.value = false
  reset()
}

/**
 * キャンペーンの作成を完了
 */
function completedCreateCampaign() {
  emit('campaignUpdated')
  is_campaign_created.value = true

  if (form.value.campaign_type === 'old_web_instantwin') {
    handleClose('')
  }
}

/**
 * キャンペーン紹介（初回表示）を閉じる
 */
function closeCampaignCreateIntro() {
  is_first_display_closed.value = true
}

defineExpose({ open, handleClose })
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.dialog-campaign-step {
  margin-bottom: $spacing-unit * 3;
}

.campaign-create-item {
  margin-top: $spacing-unit * 2;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  margin-top: $spacing-unit * 3;

  .footer-right {
    display: flex;

    .el-button:not(:first-child) {
      margin-left: $spacing-unit;
    }
  }
}

.note-message {
  margin-top: $spacing-unit;
}

:deep(.el-dialog) {
  .el-dialog__header {
    padding: $spacing-unit * 2;
    background-color: $background-dark-color;
  }

  .el-dialog__body {
    padding: $spacing-unit * 2;
  }

  .el-dialog__footer {
    padding: 0 $spacing-unit * 2 $spacing-unit * 2;
  }

  .el-dialog__title,
  .el-dialog__headerbtn .el-dialog__close {
    color: $text-color-white;
  }
}
</style>
